import { formatAddress } from '@/helpers/formatAddress';
import { formatPhoneNumber } from '@/helpers/formatPhoneNumber';
import {
  isPackageEditable,
  PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE,
  shouldShowPackageUnderReviewAlert,
} from '@/helpers/packageEditability';
import usePermissions from '@/hooks/usePermissions';
import { useToastSuccessSavedPackageMessage } from '@/hooks/useSavedPackageMessage';
import { useGetDocumentsQuery } from '@/services/documents';
import {
  useSaveInstallGeneratorPackageMutation,
  useSubmitInstallGeneratorPackageMutation,
} from '@/services/installGeneratorPackage';
import { Alert, DateInput, toast, useOpenClose } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';

import {
  Applicant,
  ApplicantType,
  DeepPartial,
  GeneratorEquipmentItem,
  GeneratorInstallPackageFileCategory,
  GeneratorSystem,
  InstallGeneratorPackage as IInstallGeneratorPackage,
  InstallationPackagePhotoCategory,
  MilestoneType,
  QueueStatus,
  QueueType,
  RequirementFlagSettingsType,
  UserPermissions,
} from 'types';

import { convertToISODateTimeString, DATE_FORMAT } from '../../helpers/dates';
import { useGetAccountQuery } from '../../services/accounts';
import { isErrorWithData } from '../../services/helpers';
import { useGetQueueByAccountIdAndTypeQuery } from '../../services/queues';
import { ReviewPackageActions } from '../../types/ReviewPackage';
import { ActionsList } from '../ActionsList';
import { useAuth } from '../auth/authProvider';
import { isInternalUser } from '../auth/RequireIsInternalUser';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';
import { ContractDocumentSection } from '../MilestonePackagePartials/ContractDocumentSection';
import { FooterRHF } from '../MilestonePackagePartials/FooterRHF';
import { AttestationSection } from '../MilestonePackagePartials/generator/AttestationSection';
import { InstallPhotos } from '../MilestonePackagePartials/generator/InstallPhotos';
import { SystemPhotos } from '../MilestonePackagePartials/generator/SystemPhotos';
import WarrantyRegistration from '../MilestonePackagePartials/hvac/WarrantyRegistration';
import { InstallContractInfo } from '../MilestonePackagePartials/InstallContractInfo';
import { QueueBanner } from '../MilestonePackagePartials/QueueBanner';
import { NotesDrawer } from '../NotesDrawer/NotesDrawer';
import { FlagBox } from '../QueueFlags/FlagBox';
import { QueueFlagsContext, QueueFlagsProvider } from '../QueueFlags/QueueFlagsContext';
import { SkeletonBox } from '../SkeletonBox';
import { SystemSection } from '../SystemDesign/generator/SystemSection';

import { InstallAttestationModal, type AttestmentPayload } from './InstallAttestationModal';

interface InstallPackageProps {
  canSubmitInstallPackage: boolean;
}

interface InstallGeneratorFormValues {
  systemInstallDate: string;
  projectAttestationCheckbox: boolean;
  systems: GeneratorSystem[];
}

const projectAttestLabel =
  'By checking this box, I confirm the following Palmetto installation requirements have been met for ' +
  'this project: Equipment Selection from AVL, Placement and Sizing, Fuel Connections and Leak  ' +
  'Testing, Permitting, and Commissioning. I have also provided all required info for Palmetto to ' +
  'register the warranty and monitoring of the system. All photos and information submitted are ' +
  'accurate and represent the as-installed system.';

export const formatInstallGeneratorPackagesData = (
  values: Record<string, any>,
): DeepPartial<IInstallGeneratorPackage> => {
  return {
    systemDesign: {
      isPreliminary: false,
      systems: values?.systems?.map((system: GeneratorSystem) => {
        return {
          name: system.name,
          equipment: {
            totalCost: system.equipment.totalCost,
            items: system.equipment.items.map((item) => ({
              type: item.type,
              manufacturer: item.manufacturer || 'unknown',
              model: item.model || 'unknown',
              quantity: item.quantity,
              serialNumbers: item.serialNumbers,
              fuelType: item.fuelType,
              ratedKw: item.ratedKw,
              runningKw: item.runningKw,
              lpPowerRating: item.lpPowerRating,
              gasPowerRating: item.gasPowerRating,
            })),
          },
          // Site prep, accessories, and discounts are temporarily commented out for the generator program; may return.
          // sitePrep: system?.sitePrep
          //   ? {
          //       totalCost: system.sitePrep.totalCost,
          //       items: (system.sitePrep?.items || []).map((item) => ({
          //         name: item.name,
          //       })),
          //     }
          //   : undefined,
          // accessories: system?.accessories
          //   ? {
          //       totalCost: system.accessories.totalCost,
          //       items: (system.accessories?.items || []).map((item) => ({
          //         name: item.name,
          //       })),
          //     }
          //   : undefined,
          // discounts: system?.discounts
          //   ? {
          //       totalCost: system.discounts.totalCost,
          //       items: (system.discounts?.items || []).map((item) => ({
          //         name: item.name,
          //       })),
          //     }
          //   : undefined,
        };
      }),
    },
    systemInstallDate: values.systemInstallDate
      ? { palmetto: convertToISODateTimeString(values.systemInstallDate) }
      : undefined,
    projectAttestation: values.projectAttestationCheckbox
      ? { attestmentText: values.projectAttestationCheckbox ? projectAttestLabel : '' }
      : undefined,
  };
};

const flattenSystemItems = (items: GeneratorEquipmentItem[]) => {
  const newItems: any[] = [];

  for (const item of items) {
    if (item.quantity > 1) {
      for (let i = 0; i < item.quantity; i++) {
        newItems.push({
          ...item,
          quantity: 1,
        });
      }
    } else {
      newItems.push({ ...item });
    }
  }
  return newItems;
};

// Photo categories that, if previously uploaded, allow us to skip the "first submission" prompt.
const documentsPreviouslyUploadedToGeneratorInstallation = [
  InstallationPackagePhotoCategory.equipmentDataPlate,
  InstallationPackagePhotoCategory.wideInstallationView,
  InstallationPackagePhotoCategory.transferSwitch,
  InstallationPackagePhotoCategory.fuelConnection,
  InstallationPackagePhotoCategory.remoteMonitoringSetup,
  InstallationPackagePhotoCategory.cellularDeviceId,
  InstallationPackagePhotoCategory.mainServicePanel,
  InstallationPackagePhotoCategory.loadControlDevices,
  InstallationPackagePhotoCategory.fieldProFuelLinePressure,
  InstallationPackagePhotoCategory.fieldProSystemRuntime,
];

const VALIDATION_ERROR = 'ValidationError';

export const InstallGeneratorPackage = ({ canSubmitInstallPackage }: InstallPackageProps) => {
  const navigate = useNavigate();
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const permissions = usePermissions();
  const isLightReachQCTech = permissions.includes(UserPermissions.lightReachQCTech);
  const userOnlyHasReadPermissions = permissions?.every((permission) => permission.includes(UserPermissions.reader));
  const { id = '', action } = useParams<{ id: string; action: ReviewPackageActions }>();
  const { data: queue, isLoading: isQueueLoading } = useGetQueueByAccountIdAndTypeQuery(
    {
      accountId: id,
      type: QueueType.installPackage,
    },
    { skip: !id },
  );
  const { getFlagsByFlagType } = useContext(QueueFlagsContext);
  const checkboxCategories = useMemo(() => Object.values(GeneratorInstallPackageFileCategory), []);
  const showCheckboxes = isInternalUser(claims, true);

  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id, { skip: !id });
  const primaryApplicant = account?.applicants.find(
    (applicant: Applicant) => applicant.type === ApplicantType.primary,
  );

  const [warrantiesConfirmed, setWarrantiesConfirmed] = useState(action !== ReviewPackageActions.review);

  const minDate = useMemo(() => DateTime.fromISO('2022-01-01').toJSDate(), []);
  const maxDate = useMemo(() => DateTime.local().endOf('day').toJSDate(), []);
  const [triggerSubmit] = useSubmitInstallGeneratorPackageMutation();
  const [triggerSave] = useSaveInstallGeneratorPackageMutation();

  const {
    isOpen: isAttestationOpen,
    handleOpen: setAttestationSubmissionOpen,
    handleClose: setAttestationSubmissionClose,
  } = useOpenClose();
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [installAttestments, setInstallAttestments] = useState<AttestmentPayload | null>(null);
  const isLoading = isAccountLoading || isQueueLoading;
  const { data: wasADocumentPreviouslyUploadedToGeneratorInstallation } = useGetDocumentsQuery(
    {
      accountId: id,
      documentTypeFilter: documentsPreviouslyUploadedToGeneratorInstallation,
    },
    { skip: !id },
  );

  const { data: installationDocuments } = useGetDocumentsQuery(
    {
      accountId: id,
      documentTypeFilter: [InstallationPackagePhotoCategory.generatorInstallationPhotos],
    },
    { skip: !id },
  );

  const customerContractFlags = getFlagsByFlagType(RequirementFlagSettingsType.customerContract);
  const generatorInstallationPhotosFlag = getFlagsByFlagType(
    RequirementFlagSettingsType.generatorInstallationPhotos,
  );
  const showInstallPhotosForInitialSubmission =
    !wasADocumentPreviouslyUploadedToGeneratorInstallation?.length &&
    !isFinCoUser &&
    (!queue || (queue && queue.status === QueueStatus.submitted));
  const showInstallPhotosForEPCUser =
    !isFinCoUser && (generatorInstallationPhotosFlag?.length || (queue && queue.status === QueueStatus.submitted));

  const canEditPackage = isPackageEditable({
    queue,
    isFinCoUser,
  });
  const showUnderReviewAlert = shouldShowPackageUnderReviewAlert({
    queue,
    isFinCoUser,
  });
  const triggerSuccessMessage = useToastSuccessSavedPackageMessage(id, MilestoneType.install);

  useEffect(() => {
    if (!action || !ReviewPackageActions[action]) {
      navigate(`/accounts/${id}`, { replace: true });
    }
    if (action === ReviewPackageActions.review && (!isFinCoUser || !isLightReachQCTech)) {
      if (queue) {
        navigate(`/accounts/${id}/installation-package/edit`, { replace: true });
      } else {
        navigate(`/accounts/${id}/installation-package/create`, { replace: true });
      }
    }
  }, [id, navigate, action, isFinCoUser, isLightReachQCTech, queue]);

  const accountInitialValues = useMemo<InstallGeneratorFormValues>(() => {
    if (!account) {
      return { systemInstallDate: '', projectAttestationCheckbox: false, systems: [] };
    }
    const systems = account?.systemDesign?.systems.reduce((acc: GeneratorSystem[], system: GeneratorSystem) => {
      const equipment = flattenSystemItems(system.equipment.items).map((item) => {
        const serialNumbers =
          item.serialNumbers && item.serialNumbers.length ? item.serialNumbers : Array(item.quantity).fill('');

        return Object.assign(item, { isSerialNumberRequired: true, serialNumbers });
      });

      const totalCost = system.equipment.totalCost || 0;

      const updatedSystem = {
        ...system,
        equipment: {
          ...system.equipment,
          totalCost,
          items: equipment,
        },
      } as GeneratorSystem;
      acc.push(updatedSystem);
      return acc;
    }, [] as GeneratorSystem[]);
    return {
      systemInstallDate: account?.systemInstallDate?.palmetto
        ? DateTime.fromISO(account.systemInstallDate.palmetto).toUTC().toFormat(DATE_FORMAT)
        : '',
      projectAttestationCheckbox: false,
      systems: systems ?? [],
    };
  }, [account]);

  const methods = useForm<InstallGeneratorFormValues>({
    defaultValues: accountInitialValues,
    values: accountInitialValues,
    mode: 'onSubmit',
  });

  const {
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const canSubmitReview = action === ReviewPackageActions.review && queue;

  const validateInstallationDocuments = useCallback((): void => {
    if (!installationDocuments?.length && !queue) {
      const validationError = new Error('At least one installation document is required.');
      validationError.name = VALIDATION_ERROR;
      throw validationError;
    }
  }, [installationDocuments, queue]);

  const performSubmit = useCallback(
    async (values: InstallGeneratorFormValues) => {
      const formValues = formatInstallGeneratorPackagesData(values);
      try {
        await triggerSubmit({
          accountId: id,
          installPackage: {
            ...formValues,
            installAttestments: installAttestments?.items ?? {},
          } as IInstallGeneratorPackage,
        }).unwrap();
        toast.success('Install Package Submitted');
        navigate(`/accounts/${id}`);
      } catch (e: any) {
        toast.error(e?.data?.message || 'Error submitting install package');
      } finally {
        setInstallAttestments(null);
      }
    },
    [id, navigate, triggerSubmit, installAttestments],
  );

  const onSubmit = useCallback(
    async (values: InstallGeneratorFormValues) => {
      try {
        validateInstallationDocuments();
      } catch (e) {
        if (e instanceof Error) {
          toast.error(e.message);
        }
        return;
      }
      await performSubmit(values);
    },
    [validateInstallationDocuments, performSubmit],
  );

  const handleModalAttest = useCallback(
    (attestments: AttestmentPayload) => {
      setInstallAttestments(attestments);
      setValue('projectAttestationCheckbox', true, { shouldValidate: true });
      setAttestationSubmissionClose();
    },
    [setValue, setAttestationSubmissionClose],
  );

  const handleClearAttestation = useCallback(() => {
    setInstallAttestments(null);
  }, []);

  const handleSave = useCallback(
    async ({
      accountId,
      data,
      targetStatus,
    }: {
      accountId: string;
      data: Record<string, any>;
      targetStatus?: QueueStatus;
    }) => {
      try {
        if (targetStatus) {
          validateInstallationDocuments();
        }
        const formValues = formatInstallGeneratorPackagesData(data);
        await triggerSave({ accountId, savePackage: formValues }).unwrap();
        triggerSuccessMessage();
      } catch (e) {
        console.error(e);
        if (isErrorWithData(e)) {
          const errorMessage = e.data.message;
          throw new Error(errorMessage, { cause: e });
        } else if (e instanceof Error && e.name === VALIDATION_ERROR) {
          throw e;
        } else {
          console.error(e);
          throw new Error('Error saving Install Package', { cause: e });
        }
      }
    },
    [triggerSave, triggerSuccessMessage, validateInstallationDocuments],
  );

  const queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser: boolean =
    !isFinCoUser &&
    queue &&
    (queue.status === QueueStatus.conditionallyApproved || queue.status === QueueStatus.approved);

  const handleDateChange = useCallback(
    (date: Date | [Date, Date] | null, fieldOnChange: (value: string) => void) => {
      if (!date || !(date instanceof Date)) return;
      fieldOnChange(DateTime.fromJSDate(date).toUTC().toFormat(DATE_FORMAT));
    },
    [],
  );

  const handleDateClear = useCallback(() => {
    setValue('systemInstallDate', '', { shouldDirty: true });
  }, [setValue]);

  if (isLoading) {
    return (
      <MainContainer>
        <FormSection title="Loading...">
          <SkeletonBox height="25px" width="100" />
        </FormSection>
      </MainContainer>
    );
  }

  const isReviewAction = action === ReviewPackageActions.review;

  return (
    <>
      <QueueFlagsProvider queueId={queue?.id}>
        <NotesDrawer isOpen={isNotesOpen} setIsOpen={setIsNotesOpen} queueType={QueueType.installPackage} />
        <MainContainer>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <ActionsList
                milestoneType={MilestoneType.install}
                accountId={queue?.account?.id}
                canSubmitPackage={canSubmitInstallPackage}
                isLoading={isLoading}
                isFirstSubmission={!queue}
                canSubmitReview={canSubmitReview}
                disableApproval={!warrantiesConfirmed}
                onSave={handleSave}
                disabled={queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser || !canEditPackage}
                initialValues={accountInitialValues}
                onSubmit={onSubmit as any}
                queue={queue}
                useRHFFooter
              />

              {queue && (queue.status === QueueStatus.rejected || isFinCoUser) ? (
                <QueueBanner
                  setIsNotesOpen={setIsNotesOpen}
                  queueType={QueueType.installPackage}
                  isFinCoUser={isFinCoUser}
                />
              ) : null}
              {showUnderReviewAlert ? (
                <Alert
                  variant="warning"
                  title="Install Package Under Review"
                  message={PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE}
                  hasIcon
                />
              ) : null}
              {isReviewAction && !canSubmitReview ? (
                <Alert
                  variant="warning"
                  title="Install Package Not Submitted"
                  message="This install package is not ready to be reviewed yet. It has not been submitted."
                  hasIcon
                />
              ) : null}
              {(isFinCoUser || (customerContractFlags?.length || 0) > 0) && (
                <ContractDocumentSection showSeparatorBorder={true} />
              )}
              <FormSection title="System Install Date">
                <FlagBox
                  dataKey="System Install Date"
                  baseCategory="systemInstallDate"
                  flagType={RequirementFlagSettingsType.systemInstallDateGenerator}
                  displayWithNoQueue
                >
                  <Controller
                    name="systemInstallDate"
                    control={methods.control}
                    rules={{ required: 'System Install Date is a required field.' }}
                    render={({ field, fieldState }) => {
                      const openToDate = field.value
                        ? DateTime.fromFormat(field.value, DATE_FORMAT).toJSDate()
                        : undefined;
                      return (
                        <DateInput
                          label=""
                          name="systemInstallDate"
                          id="systemInstallDate"
                          dateFormat={DATE_FORMAT}
                          width="2xl"
                          value={field.value}
                          datePickerProps={{
                            maxDate,
                            minDate,
                            onChange: (date: Date | [Date, Date] | null) => handleDateChange(date, field.onChange),
                            openToDate,
                          }}
                          textInputProps={{ onClear: handleDateClear }}
                          isDisabled={isSubmitting || !canEditPackage}
                          error={fieldState.isTouched && fieldState.error?.message}
                        />
                      );
                    }}
                  />
                </FlagBox>
              </FormSection>
              {isFinCoUser && (
                <FormSection title="Customer Information">
                  <table>
                    <tbody>
                      <tr>
                        <td>Partner Licensed Org:</td>
                        <td>
                          <Link to={`/settings/organizations/${account?.licensedOrganizationId}`}>
                            {account?.licensedOrganizationId?.toUpperCase() ?? ''}
                          </Link>
                        </td>
                      </tr>
                      <tr>
                        <td>Partner Org:</td>
                        <td>
                          <Link to={`/settings/organizations/${account?.organizationId}`}>
                            {account?.organizationId?.toUpperCase() ?? ''}
                          </Link>
                        </td>
                      </tr>
                      <tr>
                        <td>Primary Contact Details:</td>
                        <td>
                          {primaryApplicant?.firstName} {primaryApplicant?.lastName}
                          {' - '}
                          {primaryApplicant?.email}
                          {' - '}
                          {primaryApplicant?.phoneNumber ? formatPhoneNumber(primaryApplicant.phoneNumber) : ''}
                        </td>
                      </tr>
                      <tr>
                        <td>Property Address:</td>
                        <td>{formatAddress(account?.address)}</td>
                      </tr>
                    </tbody>
                  </table>
                </FormSection>
              )}
              {showInstallPhotosForInitialSubmission ? (
                <InstallPhotos
                  showCheckboxes={showCheckboxes}
                  checkboxCategories={checkboxCategories}
                  isPackageEditable={canEditPackage}
                />
              ) : (
                <>
                  {(isFinCoUser || showInstallPhotosForEPCUser) && (
                    <InstallPhotos
                      showCheckboxes={showCheckboxes}
                      checkboxCategories={checkboxCategories}
                      isPackageEditable={canEditPackage}
                    />
                  )}
                  {isReviewAction && (
                    <FormSection>
                      <InstallContractInfo
                        isSubmitting={isSubmitting}
                        isLoading={isLoading}
                        userOnlyHasReadPermissions={userOnlyHasReadPermissions}
                        isPackageEditable={canEditPackage}
                      />
                    </FormSection>
                  )}
                  <SystemPhotos
                    showCheckboxes={showCheckboxes}
                    checkboxCategories={checkboxCategories}
                    isPackageEditable={canEditPackage}
                    isFinCoUser={isFinCoUser}
                  />
                </>
              )}
              <SystemSection
                isInstallPackage
                isPreliminary={account?.systemDesign?.isPreliminary}
                isPackageEditable={canEditPackage}
              />
              {isFinCoUser && isReviewAction && (
                <WarrantyRegistration onValidityChange={setWarrantiesConfirmed} isPackageEditable={canEditPackage} />
              )}
              {!isReviewAction && (
                <AttestationSection
                  projectAttestLabel={projectAttestLabel}
                  isSubmitting={isSubmitting}
                  isPackageEditable={canEditPackage}
                  onOpenAttestation={setAttestationSubmissionOpen}
                  onClearAttestation={handleClearAttestation}
                />
              )}
              <FooterRHF
                canSubmitPackage={canSubmitInstallPackage}
                isLoading={isLoading}
                isFirstSubmission={!queue}
                canSubmitReview={canSubmitReview}
                disableApproval={!warrantiesConfirmed}
                onSave={handleSave}
                disabled={queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser || !canEditPackage}
              />
            </form>
          </FormProvider>
          {isAttestationOpen && (
            <InstallAttestationModal
              isOpen={isAttestationOpen}
              onDismiss={setAttestationSubmissionClose}
              onAttest={handleModalAttest}
            />
          )}
        </MainContainer>
      </QueueFlagsProvider>
    </>
  );
};
